/**
 * Viewer Settings panel — personal preferences dialog.
 *
 * Lives next to the OwnerBar in the top strip. Visible to ALL visitors
 * (not just the owner). Surfaces personal knobs that override owner
 * defaults (phosphor / cabinet skin / room / effects / boot ident /
 * patron perks). Volume is no longer surfaced here — it's a live
 * remote-only value, persisted across reloads for stickiness but not
 * exposed as a "default" knob. Starting-channel modes were also removed:
 * every fresh mount now lands on a random real channel (see App.js's
 * lineup effect) so the front page feels like flipping-on a real TV.
 *
 * Storage = localStorage via `lib/viewerPrefs.js`. When the viewer signs
 * in the same shape is hydrated from the server.
 */
import React, { useEffect, useState } from "react";
import { Settings, X, LogOut, Mail } from "lucide-react";
import { getPrefs, setPrefs, resetPrefs } from "@/lib/viewerPrefs";
import { getUnlocked } from "@/lib/trophies";
import { useAuth } from "@/lib/auth";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

// ─── Magic-link sign-in ─────────────────────────────────────────────────
// Small, self-contained form that swaps into the sign-in row when the
// viewer clicks "OR EMAIL". Kept inside this file (rather than pulled
// out to its own component) because it's a single small stateful piece
// that only ever lives inside the PREFS panel.
function MagicLinkForm({ onBack }) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error
  const [message, setMessage] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email || status === "sending") return;
    setStatus("sending");
    setMessage("");
    try {
      const res = await fetch(`${API}/auth/magic-request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: email.trim() }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `Request failed (${res.status})`);
      }
      setStatus("sent");
      setMessage("Check your inbox for a sign-in link. It expires in 15 minutes.");
    } catch (err) {
      setStatus("error");
      setMessage((err && err.message) || "Couldn't send the email.");
    }
  };

  if (status === "sent") {
    return (
      <div className="w-full" data-testid="magic-sent-state">
        <div className="text-[12px] tracking-[0.22em] mb-1 text-[var(--phosphor)]">▶ LINK SENT</div>
        <div className="text-[12px] opacity-75 leading-snug mb-2">{message}</div>
        <div className="flex gap-1.5">
          <button
            type="button"
            onClick={onBack}
            className="text-[11px] tracking-[0.22em] opacity-70 hover:opacity-100 underline decoration-dotted underline-offset-2"
            data-testid="magic-sent-back"
          >
            ◄ back
          </button>
          <span className="opacity-40 text-[11px]">·</span>
          <button
            type="button"
            onClick={() => { setStatus("idle"); setMessage(""); }}
            className="text-[11px] tracking-[0.22em] opacity-70 hover:opacity-100 underline decoration-dotted underline-offset-2"
            data-testid="magic-sent-resend"
          >
            didn&apos;t arrive? send again
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="w-full" data-testid="magic-form">
      <div className="text-[12px] tracking-[0.22em] opacity-85 mb-1">SIGN IN WITH EMAIL</div>
      <div className="flex gap-1">
        <input
          type="email"
          required
          autoFocus
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={status === "sending"}
          className="flex-1 min-w-0 bg-black/40 border border-[color-mix(in_srgb,var(--phosphor)_40%,transparent)] focus:border-[var(--phosphor)] outline-none px-2 py-1 text-[13px] tracking-[0.12em] text-[var(--phosphor)]"
          style={{ fontFamily: "VT323, monospace" }}
          data-testid="magic-email-input"
        />
        <button
          type="submit"
          disabled={status === "sending" || !email}
          className="text-[11px] tracking-[0.22em] border border-[var(--phosphor)] px-2 py-1 hover:bg-[var(--phosphor)] hover:text-black disabled:opacity-50 transition-colors"
          data-testid="magic-submit"
        >
          {status === "sending" ? "SENDING…" : "SEND LINK"}
        </button>
      </div>
      <div className="mt-1 flex items-center gap-2 text-[11px]">
        <button
          type="button"
          onClick={onBack}
          className="opacity-65 hover:opacity-100 underline decoration-dotted underline-offset-2 tracking-[0.22em]"
          data-testid="magic-back"
        >
          ◄ back
        </button>
        {status === "error" && (
          <span className="opacity-80 text-red-400 truncate" data-testid="magic-error">
            {message}
          </span>
        )}
      </div>
    </form>
  );
}

export default function ViewerSettings({ channels }) {
  const [open, setOpen] = useState(false);

  // Phase 2.3.79g-r3.6 — mobile PREFS opens the authoritative ViewerSettings state directly.
  React.useEffect(() => {
    const openFromMobile = () => setOpen(true);
    window.addEventListener("sltv:mobile-open-prefs", openFromMobile);
    return () => window.removeEventListener("sltv:mobile-open-prefs", openFromMobile);
  }, []);
  const [draft, setDraft] = useState(getPrefs());
  const [showMagicForm, setShowMagicForm] = useState(false);
  const { user, loading: authLoading, signOut } = useAuth();
  // Whether this viewer has unlocked the PATRON trophy — gates the
  // "HIDE DISTRACTIONS" perk toggle so non-patrons never see it. Kept
  // in state (rather than read on every render) so a mid-session
  // unlock via the return-from-BMC bumper flips the option on
  // immediately when the panel re-opens.
  const [isPatron, setIsPatron] = useState(false);
  useEffect(() => {
    const refresh = () => {
      try { setIsPatron(getUnlocked().has("patron")); }
      catch (e) { setIsPatron(false); }
    };
    refresh();
    window.addEventListener("crt:trophy-unlocked", refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener("crt:trophy-unlocked", refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);

  // Auto values published by App.js (cabinet-scale) and Rail.js
  // (guide-fit-count). We use these to park the AUTO-mode slider thumbs
  // at the actual current effective value — otherwise the CABINET SIZE
  // thumb parks at 100 (25% of the 75-175 track) and the TILES PER ROW
  // thumb parks at 3 (min), which viewers misread as "the cabinet is
  // 100%" / "showing 3 tiles" even when the label says AUTO. Both
  // values are broadcast on mount + on every recompute (viewport
  // resize etc), so this stays in sync without additional wiring.
  //
  // Seed from `window.__crt*` on mount — the publishers cache their
  // latest value there so a ViewerSettings that mounts AFTER the
  // initial dispatch (e.g. after power-on / guide-open forces a
  // re-render) still reads the right value instead of falling back
  // to the useState default. Falls back to reasonable defaults when
  // the value hasn't been published yet (first paint / no guide open).
  const [autoCabinetScale, setAutoCabinetScale] = useState(() => {
    const cached = typeof window !== "undefined" ? window.__crtAutoCabinetScale : null;
    return Number.isFinite(cached) ? cached : 1;
  });
  const [autoTilesPerRow, setAutoTilesPerRow] = useState(() => {
    const cached = typeof window !== "undefined" ? window.__crtGuideFitCount : null;
    return Number.isFinite(cached) ? cached : 8;
  });
  useEffect(() => {
    // Re-seed from window whenever this effect runs — belt-and-braces
    // in case the publisher fired between initial useState and this
    // effect registration.
    if (typeof window !== "undefined") {
      if (Number.isFinite(window.__crtAutoCabinetScale)) setAutoCabinetScale(window.__crtAutoCabinetScale);
      if (Number.isFinite(window.__crtGuideFitCount)) setAutoTilesPerRow(window.__crtGuideFitCount);
    }
    const onCabinet = (e) => { if (Number.isFinite(e.detail)) setAutoCabinetScale(e.detail); };
    const onTiles = (e) => { if (Number.isFinite(e.detail)) setAutoTilesPerRow(e.detail); };
    window.addEventListener("crt:auto-cabinet-scale", onCabinet);
    window.addEventListener("crt:guide-fit-count", onTiles);
    return () => {
      window.removeEventListener("crt:auto-cabinet-scale", onCabinet);
      window.removeEventListener("crt:guide-fit-count", onTiles);
    };
  }, []);
  // Also refresh from the window cache every time PREFS opens — this
  // is the moment the user cares about, and cheap enough to just
  // re-read rather than trust the pub/sub to have delivered.
  useEffect(() => {
    if (!open || typeof window === "undefined") return;
    if (Number.isFinite(window.__crtAutoCabinetScale)) setAutoCabinetScale(window.__crtAutoCabinetScale);
    if (Number.isFinite(window.__crtGuideFitCount)) setAutoTilesPerRow(window.__crtGuideFitCount);
  }, [open]);


  // Hydrate prefs from server whenever the signed-in user changes. Server
  // is the source of truth when logged in — but we DEFENSIVELY MERGE with
  // any local values the server response is missing. This protects against
  // schema drift where the client learned a new pref (e.g. `cabinet_scale`)
  // before the server did, so an older-server response would otherwise
  // clobber the local value with the schema default (null → AUTO).
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/viewer/prefs`, { credentials: "include" });
        if (!res.ok || cancelled) return;
        const serverPrefs = await res.json();
        // Merge: start from local, overlay server. Server value wins
        // UNLESS the server response is null/undefined for a key while
        // local holds a real value — that means the server hasn't
        // stored the field yet (fresh signed-in viewer, or schema drift
        // between client + server versions). In that case we keep the
        // local value so a mid-session APPLY isn't clobbered by the
        // very next auto-hydrate.
        const localPrefs = getPrefs();
        const merged = { ...localPrefs };
        for (const [k, v] of Object.entries(serverPrefs || {})) {
          if (v === undefined) continue;
          if (v === null && localPrefs[k] != null) continue;
          merged[k] = v;
        }
        // setPrefs broadcasts crt:prefs-updated, which the rest of the
        // app already listens for — so volume/phosphor-override/etc all
        // reflect the server state without extra wiring.
        setPrefs(merged);
        setDraft(merged);
      } catch (e) { /* noop */ }
    })();
    return () => { cancelled = true; };
  }, [user]);

  // Background sync — when signed in, push the whole prefs blob to the
  // server whenever it changes (favourites toggles, volume nudge from the
  // remote, etc). Debounced via crt:prefs-updated batching — listeners run
  // synchronously so the latest blob always wins.
  useEffect(() => {
    if (!user) return undefined;
    const flush = async () => {
      try {
        await fetch(`${API}/viewer/prefs`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(getPrefs()),
        });
      } catch (e) { /* noop — next change will retry */ }
    };
    const onChange = () => flush();
    window.addEventListener("crt:prefs-updated", onChange);
    return () => window.removeEventListener("crt:prefs-updated", onChange);
  }, [user]);

  // Reset draft whenever the panel opens so closing-without-saving doesn't
  // leak unsaved state. (Saves are explicit via the APPLY button.)
  // Also drops any live-preview overrides (cabinet-scale + tiles-per-row)
  // when the panel closes, so dismissing PREFS without APPLY reverts the
  // visible state to the persisted value. The APPLY handler dispatches
  // `crt:prefs-updated` right before setOpen(false) — the preview-end
  // listeners re-read the persisted value from getPrefs(), so the
  // committed value replaces the preview seamlessly.
  useEffect(() => {
    if (open) {
      setDraft(getPrefs());
    } else {
      window.dispatchEvent(new CustomEvent("crt:cabinet-scale-preview-end"));
      window.dispatchEvent(new CustomEvent("crt:guide-tiles-preview-end"));
    }
  }, [open]);

  // `realChannels` used to feed the starting-channel picker — that pref
  // was removed (App.js always random-picks a real channel on mount) so
  // the filter is no longer referenced here.

  const phosphorChoices = [
    { id: null,     label: "USE OWNER" },
    { id: "green",  label: "GREEN" },
    { id: "amber",  label: "AMBER" },
    { id: "blue",   label: "BLUE" },
    { id: "white",  label: "WHITE" },
  ];

  const skinChoices = [
    { id: null,             label: "USE OWNER",  swatch: "linear-gradient(135deg,#444,#222)" },
    { id: "wood",           label: "WOOD",       swatch: "linear-gradient(135deg,#5a3d1a,#2e1c11)" },
    { id: "obsidian",       label: "BLACK",      swatch: "linear-gradient(135deg,#222,#000)" },
    { id: "pearl",          label: "PEARL",      swatch: "linear-gradient(135deg,#f5f0e3,#d8d2c0)" },
    { id: "brushed-steel",  label: "STEEL",      swatch: "linear-gradient(135deg,#7a7f87,#3a3f47)" },
    { id: "neon",           label: "NEON",       swatch: "linear-gradient(135deg,#ff00ff,#00ffff)" },
  ];



  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="viewer-settings-toggle"
        title="Personal viewer settings"
        className="flex items-center gap-1.5 px-2 py-1.5 text-[10px] tracking-[0.3em] border border-neutral-700 text-neutral-300 hover:border-[var(--phosphor)] hover:text-[var(--phosphor)] transition-colors"
      >
        <Settings size={12} />
        PREFS
      </button>

      {open && (
        <div
          className="absolute top-full left-0 mt-2 w-[420px] max-h-[80vh] overflow-y-auto bg-black border-2 border-[var(--phosphor)] p-4 text-[var(--phosphor)] shadow-[0_0_20px_color-mix(in_srgb,var(--phosphor)_35%,transparent)] z-30"
          data-testid="viewer-settings-panel"
          style={{ fontFamily: "VT323, monospace", fontSize: "15px", lineHeight: 1.35 }}
        >
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm tracking-[0.3em]">┌─ MY PREFS ─┐</div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setOpen(false)}
                className="text-[var(--phosphor)]"
                data-testid="viewer-settings-close"
              >
                <X size={16} />
              </button>
            </div>
          </div>

          {/* Sign-in row — two paths, keeps the row visually simple: */}
          {/*   • Anonymous: shows Google + "or email" toggle */}
          {/*   • Anonymous + email chosen: swaps in the magic-link form */}
          {/*   • Signed in: shows avatar + name + sign-out */}
          <div className="border border-[color-mix(in_srgb,var(--phosphor)_30%,transparent)] p-2 mb-3 flex items-center gap-2">
            {authLoading ? (
              <div className="text-[12px] opacity-65 tracking-[0.18em]">CHECKING SESSION…</div>
            ) : user ? (
              <>
                {user.picture && (
                  <img src={user.picture} alt="" className="w-6 h-6 rounded-full border border-[var(--phosphor)]" data-testid="auth-user-picture" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-[12px] tracking-[0.18em] truncate" data-testid="auth-user-name">{user.name}</div>
                  <div className="text-[12px] opacity-55 truncate">SYNCED ACROSS DEVICES</div>
                </div>
                <button
                  onClick={signOut}
                  data-testid="auth-sign-out"
                  className="flex items-center gap-1 text-[12px] tracking-widest border border-[color-mix(in_srgb,var(--phosphor)_40%,transparent)] px-2 py-1 hover:border-[var(--phosphor)] transition-colors"
                  title="Sign out (prefs stay local)"
                >
                  <LogOut size={11} />
                  OUT
                </button>
              </>
            ) : showMagicForm ? (
              <MagicLinkForm onBack={() => setShowMagicForm(false)} />
            ) : (
              <div className="flex-1 flex flex-col gap-1.5">
                <div className="flex items-center gap-2">
                  <div className="flex-1 text-[12px] opacity-65 leading-snug">
                    Sign in to sync prefs, favourites &amp; ★ PATRON badge across devices.
                  </div>
                  <button
                    onClick={() => setShowMagicForm(true)}
                    data-testid="auth-sign-in"
                    className="flex items-center gap-1.5 text-[12px] tracking-widest border border-[var(--phosphor)] px-2 py-1 hover:bg-[var(--phosphor)] hover:text-black transition-colors"
                  >
                    <Mail size={12} />
                    EMAIL
                  </button>
                </div>

              </div>
            )}
          </div>

          <div className="text-[12px] opacity-65 mb-3 leading-snug">
            {user ? "Synced to the cloud." : "Stored on this browser only."}
          </div>

          {/* Channel Guide tile density — lets ultrawide viewers force
              a specific tiles-per-row count instead of relying on the
              auto-fit algorithm. `null` (AUTO) keeps the legacy
              measured behaviour; a number from 3-20 hard-caps the
              rail's page size. Only affects the Channel Guide rails
              (not the Channel Browse grid). Rendered near the top so
              layout-tuning is easy to find. */}
          <div className="mb-4 border border-[color-mix(in_srgb,var(--phosphor)_30%,transparent)] p-2">
            <div className="flex items-center justify-between text-[12px] mb-1">
              <span className="tracking-[0.22em] opacity-85">GUIDE · TILES PER ROW</span>
              <span className="tabular-nums opacity-80" data-testid="pref-tiles-per-row-value">
                {draft.guide_tiles_per_row == null ? `AUTO (${autoTilesPerRow})` : draft.guide_tiles_per_row}
              </span>
            </div>
            <input
              type="range"
              min={3}
              max={20}
              step={1}
              // In AUTO mode, park the thumb at the current auto-fit
              // value so the slider visually communicates what's really
              // happening. Dragging FROM here toggles the pref to a
              // manual pin at the same value — a natural transition.
              value={draft.guide_tiles_per_row == null
                ? Math.max(3, Math.min(20, autoTilesPerRow))
                : draft.guide_tiles_per_row}
              onChange={(e) => {
                const val = Number(e.target.value);
                // Defensive: if we're currently in AUTO mode and the
                // event value equals the current auto-fit count, the
                // user probably just clicked the slider TRACK at the
                // thumb's parked position (some browsers fire onChange
                // on click even without a drag). Ignore — otherwise a
                // stray click silently converts AUTO into a manual pin
                // at the auto value, which then sticks after refresh.
                if (draft.guide_tiles_per_row == null && val === autoTilesPerRow) return;
                setDraft((d) => ({ ...d, guide_tiles_per_row: val }));
                // Live-preview: Rail.js listens and re-paginates immediately.
                window.dispatchEvent(new CustomEvent("crt:guide-tiles-preview", { detail: val }));
              }}
              data-testid="pref-tiles-per-row-slider"
              className="w-full accent-[var(--phosphor)]"
            />
            <div className="flex items-center justify-between text-[11px] mt-1">
              <div className="opacity-55 leading-snug">
                cap the Channel Guide rail width — handy on ultrawide monitors
              </div>
              <button
                type="button"
                onClick={() => {
                  setDraft((d) => ({ ...d, guide_tiles_per_row: null }));
                  window.dispatchEvent(new CustomEvent("crt:guide-tiles-preview", { detail: null }));
                }}
                data-testid="pref-tiles-per-row-auto"
                className="text-[11px] tracking-[0.22em] opacity-70 hover:opacity-100 underline decoration-dotted underline-offset-2 shrink-0 ml-2"
                title="Auto-fit as many tiles as the container width allows"
              >
                AUTO
              </button>
            </div>
          </div>

          {/* Cabinet size — grows or shrinks the CRT + remote combo as a
              whole. AUTO detects viewport width and scales the pair up
              on wide monitors; manual pin (75%-175%) hard-locks the
              scale for viewers who want a specific look. Live-previews
              while dragging (dispatches `crt:cabinet-scale-preview`,
              App.js applies it to `--cabinet-scale` without touching
              persisted prefs). Reverts on close-without-APPLY. */}
          <div className="mb-4 border border-[color-mix(in_srgb,var(--phosphor)_30%,transparent)] p-2">
            <div className="flex items-center justify-between text-[12px] mb-1">
              <span className="tracking-[0.22em] opacity-85">CABINET SIZE</span>
              <span className="tabular-nums opacity-80" data-testid="pref-cabinet-scale-value">
                {draft.cabinet_scale == null
                  ? `AUTO (${Math.round(autoCabinetScale * 100)}%)`
                  : `${Math.round(draft.cabinet_scale * 100)}%`}
              </span>
            </div>
            <input
              type="range"
              min={75}
              max={175}
              step={5}
              // Same treatment as TILES PER ROW: in AUTO mode park the
              // thumb at the current auto-computed scale so the visual
              // matches reality. Dragging FROM here transitions to a
              // manual pin at the same value — no visual jump.
              value={draft.cabinet_scale == null
                ? Math.max(75, Math.min(175, Math.round(autoCabinetScale * 100)))
                : Math.round(draft.cabinet_scale * 100)}
              onChange={(e) => {
                const val = Number(e.target.value) / 100;
                // Same defensive check as TILES PER ROW: clicking the
                // slider track at the parked-in-AUTO position was
                // silently converting AUTO into a manual pin at the
                // auto value. Compare with a small tolerance because
                // the auto value is snapped to 0.05 but a browser
                // click can round to the nearest 5% step (0.01
                // precision noise).
                if (draft.cabinet_scale == null && Math.abs(val - autoCabinetScale) < 0.005) return;
                setDraft((d) => ({ ...d, cabinet_scale: val }));
                // Live-preview: broadcast the draft scale so App.js can
                // apply it to the layout's --cabinet-scale CSS var in
                // real time. Nothing is persisted — APPLY still commits.
                window.dispatchEvent(new CustomEvent("crt:cabinet-scale-preview", { detail: val }));
              }}
              data-testid="pref-cabinet-scale-slider"
              className="w-full accent-[var(--phosphor)]"
            />
            <div className="flex items-center justify-between text-[11px] mt-1">
              <div className="opacity-55 leading-snug">
                scale the TV + remote — AUTO fits your monitor width
              </div>
              <button
                type="button"
                onClick={() => {
                  setDraft((d) => ({ ...d, cabinet_scale: null }));
                  // Live-preview AUTO too — App.js listens and falls back
                  // to its computed viewport-based scale.
                  window.dispatchEvent(new CustomEvent("crt:cabinet-scale-preview", { detail: null }));
                }}
                data-testid="pref-cabinet-scale-auto"
                className="text-[11px] tracking-[0.22em] opacity-70 hover:opacity-100 underline decoration-dotted underline-offset-2 shrink-0 ml-2"
                title="Auto-scale to viewport width"
              >
                AUTO
              </button>
            </div>
          </div>

          {/* Toggles — split into two named groups so the panel stays
              scannable as more options accumulate. */}
          {[
            {
              heading: "DISPLAY EFFECTS",
              items: [
                { id: "power_on_animation", label: "POWER-ON ANIMATION", hint: "phosphor flash + scan-line warmup when you turn the TV on" },
              ],
            },
          ].map((group) => (
            <div key={group.heading} className="mb-4">
              <div className="text-[11px] tracking-[0.35em] opacity-60 mb-1.5">
                ◢ {group.heading} ◣
              </div>
              <div className="space-y-1.5">
                {group.items.map((t) => (
                  <label key={t.id} className="flex items-start gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={!!draft[t.id]}
                      onChange={(e) => setDraft((d) => ({ ...d, [t.id]: e.target.checked }))}
                      data-testid={`pref-${t.id.replace(/_/g, "-")}`}
                      className="accent-[var(--phosphor)] mt-0.5"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-[12px] tracking-[0.22em]">{t.label}</div>
                      <div className="text-[11px] opacity-55 leading-snug">{t.hint}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          ))}

          {/* ★ PATRON perk — only surfaced to viewers who've unlocked
              the PATRON trophy via the return-from-BMC bumper. Hides
              the top-left channel-logo watermark + the bottom-left
              SCARTLEAD.TV bezel wordmark, leaving the tube completely
              clean. Auto-enabled on first unlock (see the effect in
              CRT.js) so tippers get the reward without needing to
              open PREFS — but always toggle-able here. Also fades in
              a gold-star bug on the bezel for visual signal. */}
          {isPatron && (
            <div
              className="mb-4 border p-2 rounded-sm"
              style={{
                borderColor: "#f5c54280",
                backgroundColor: "rgba(245,197,66,0.04)",
              }}
              data-testid="patron-perks-group"
            >
              <div
                className="text-[11px] tracking-[0.35em] mb-1.5"
                style={{ color: "#f5c542", opacity: 0.85 }}
              >
                ◢ ★ PATRON PERKS ◣
              </div>
              <label className="flex items-start gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={!!draft.hide_distractions}
                  onChange={(e) => setDraft((d) => ({ ...d, hide_distractions: e.target.checked }))}
                  data-testid="pref-hide-distractions"
                  className="accent-[var(--phosphor)] mt-0.5"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-[12px] tracking-[0.22em]" style={{ color: "#f5c542" }}>
                    HIDE ON-SCREEN DISTRACTIONS
                  </div>
                  <div className="text-[11px] opacity-70 leading-snug" style={{ color: "#f5c542" }}>
                    hide the channel-logo bug, the bezel wordmark and the live-broadcast overlay for a picture-clean tube
                  </div>
                </div>
              </label>
            </div>
          )}

          <div
            className="border border-[color-mix(in_srgb,var(--phosphor)_28%,transparent)] bg-black/20 p-3 mb-3"
            data-testid="pref-picture-mode"
          >
            <div className="flex items-center justify-between gap-3 mb-2">
              <div>
                <div className="text-[10px] tracking-[0.24em] font-bold">CRT PICTURE FORMAT</div>
                <div className="text-[9px] opacity-55 mt-1">classic 4:3 widescreen presentation</div>
              </div>
              <div className="text-[10px] tracking-[0.15em] text-[var(--phosphor)]">
                {(draft.picture_mode || "letterbox").toUpperCase()}
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-1.5">
              {[
                ["letterbox", "LETTERBOX"],
                ["14:9", "14:9"],
                ["pan-scan", "PAN & SCAN"],
                ["cinema-zoom", "CINEMA ZOOM"],
                ["stretch", "STRETCH"],
              ].map(([id, label]) => {
                const selected = (draft.picture_mode || "letterbox") === id;
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setDraft((d) => ({ ...d, picture_mode: id }))}
                    className={`px-2 py-2 border text-[9px] tracking-[0.12em] transition-colors ${
                      selected
                        ? "border-[var(--phosphor)] text-[var(--phosphor)] bg-[color-mix(in_srgb,var(--phosphor)_10%,black)]"
                        : "border-white/15 text-white/55 hover:border-white/30"
                    }`}
                    data-testid={`picture-mode-${id}`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Phase 2.3.84 — tube glow itself is always enabled.
              Viewers can tune its intensity and optionally enable ambient
              breath or programme-reactive animation. */}
          <div
            className="mb-4 border border-[color-mix(in_srgb,var(--phosphor)_30%,transparent)] p-3 space-y-2"
            data-testid="tube-glow-controls"
          >
            <div className="flex items-center justify-between gap-3">
              <div className="text-[11px] tracking-[0.3em] opacity-70">
                ◢ CRT TUBE GLOW ◣
              </div>
              <div className="text-[9px] tracking-[0.2em] text-[var(--phosphor)] opacity-80">
                ALWAYS ON
              </div>
            </div>

            <div className="pt-2 mt-1 border-t border-[color-mix(in_srgb,var(--phosphor)_22%,transparent)] space-y-2">
              <div>
                <div className="flex items-center justify-between text-[12px] mb-1">
                  <span className="opacity-70 tracking-[0.2em]">INTENSITY</span>
                  <span className="tabular-nums opacity-80">{Number(draft.tube_glow_intensity ?? 100)}</span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={100}
                  step={1}
                  value={Number(draft.tube_glow_intensity ?? 100)}
                  onChange={(e) => setDraft((d) => ({ ...d, tube_glow_intensity: Number(e.target.value) }))}
                  data-testid="pref-tube-glow-intensity"
                  className="w-full accent-[var(--phosphor)]"
                />
              </div>

              <label className="flex items-start gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={!!draft.tube_glow_animated}
                  onChange={(e) => setDraft((d) => ({ ...d, tube_glow_animated: e.target.checked }))}
                  data-testid="pref-tube-glow-animated"
                  className="accent-[var(--phosphor)] mt-0.5"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-[12px] tracking-[0.22em]">BREATH ANIMATION</div>
                  <div className="text-[11px] opacity-55 leading-snug">
                    optional slow, subtle tube-light pulse
                  </div>
                </div>
              </label>

              <label className="flex items-start gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={!!draft.tube_glow_audio_reactive}
                  onChange={(e) => setDraft((d) => ({ ...d, tube_glow_audio_reactive: e.target.checked }))}
                  data-testid="pref-tube-glow-audio-reactive"
                  className="accent-[var(--phosphor)] mt-0.5"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-[12px] tracking-[0.22em]">REACT TO AUDIO</div>
                  <div className="text-[11px] opacity-55 leading-snug">
                    programme-driven tube pulse — real audio where available; playback-reactive fallback on protected streams
                  </div>
                </div>
              </label>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => { resetPrefs(); setDraft(getPrefs()); setOpen(false); }}
              data-testid="pref-reset"
              className="flex-1 border border-[color-mix(in_srgb,var(--phosphor)_50%,transparent)] py-1.5 text-[12px] tracking-widest hover:bg-[color-mix(in_srgb,var(--phosphor)_8%,transparent)] transition-colors"
            >
              RESET DEFAULTS
            </button>
            <button
              onClick={async () => {
                setPrefs(draft);
                // Also push to the server when signed in so the same blob
                // appears on the viewer's other devices. Local save is
                // already complete by the time this fetch resolves so a
                // network blip just means "synced next time".
                if (user) {
                  try {
                    await fetch(`${API}/viewer/prefs`, {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      credentials: "include",
                      body: JSON.stringify(draft),
                    });
                  } catch (e) { /* noop */ }
                }
                setOpen(false);
              }}
              data-testid="pref-save"
              className="flex-1 border border-[var(--phosphor)] py-1.5 text-[12px] tracking-widest hover:bg-[var(--phosphor)] hover:text-black transition-colors"
            >
              APPLY
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
